import { QueryKeys } from "@/constants";
import useAuth, { AuthMode } from "@/context/auth-provider";
import { useGroupProvider } from "@/context/group-provider";
import type { Demographics, PlanCoverageInfo } from "@/model/demographics";
import { queryClient } from "./use-query-client";
import { useMemo } from "react";
import { isCoverageActiveOrExtended } from "@/lib/utils";

export type IUserInfo = {
  mode: AuthMode;
  loginGroupId: string;
  employeeId: string;
  memberId: string;
  groupId: string;
  clientLogo: string | null;
  divisionId: string;
  divisionName: string;
  groupName: string;
  accessToken: string;
  username: string;
  age: number;
  planId: string;
  planDesc: string;
  email: string;
  phone: string;
  firstname: string;
  lastname: string;
  dob: string;
  zipCode: string;
  relationship: string;
  isMfaEnabled: boolean;
  serviceProvider: string;
  serviceProviderWebsite: string;
  serviceProviderPhone: string;
  mbrId: string;
  supportNumber: string;
  planCoverageInfo: PlanCoverageInfo[];
  lat: number;
  lng: number;
  plainEmployeeId: string;
  address: string;
  mbrStreet1?: string;
  mbrStreet2?: string;
  city?: string;
  state?: string;
  mbrEffDate?: string;
  mbrTermDate?: string;

  chatbotDetails: {
    serviceDisplayEmail?: string;
    serviceProviderWebsite?: string;
    serviceProviderEmail?: string;
    serviceProvider?: string;
    serviceProviderPhoneNumber?: string;
    displayWebsite?: string;
  };
};

const useUserInfo = (): IUserInfo => {
  const { authData } = useAuth();
  const { groupData } = useGroupProvider();

  const demographicsData = queryClient.getQueryData([
    QueryKeys.DEMOGRAPHICS,
    authData?.user?.username,
  ]) as Demographics;

  const info = useMemo<IUserInfo>(
    () => ({
      mode: authData?.mode || AuthMode.MEMBER,
      loginGroupId: demographicsData?.zphLoginGroupId
        ? demographicsData?.zphLoginGroupId
        : groupData?.entered_id || "",
      employeeId: demographicsData?.employeeId || "",
      memberId: demographicsData?.memberId || "",
      clientLogo: groupData?.client_logo || null,
      groupId: demographicsData?.groupId
        ? demographicsData?.groupId
        : groupData?.group_id || "",
      divisionId: demographicsData?.divisionId
        ? demographicsData?.divisionId
        : groupData?.division_id || "",
      divisionName: demographicsData?.divisionName || "",
      groupName: demographicsData?.groupName
        ? demographicsData?.groupName
        : groupData?.group_name || "",
      accessToken: authData?.user?.access_token || "",
      username: authData?.user?.username || "",
      age: demographicsData?.age,
      planId: demographicsData?.planId || "",
      planDesc: demographicsData?.planDesc || "",
      email: demographicsData?.email || "",
      phone: demographicsData?.phoneNumber || "",
      firstname: demographicsData?.firstName || "",
      lastname: demographicsData?.lastName || "",
      dob: demographicsData?.dob || "",
      zipCode: demographicsData?.zipCode || "",
      relationship: demographicsData?.relationship || "",
      isMfaEnabled: authData?.user?.is_mfa_enabled || false,
      serviceProvider: groupData?.csrTeamName ? groupData?.csrTeamName : "",
      serviceProviderPhone: groupData?.support_number
        ? groupData?.support_number
        : "",
      serviceProviderWebsite: groupData?.website ? groupData?.website : "",
      mbrId: demographicsData?.mbrId ? demographicsData?.mbrId : "",
      supportNumber: groupData?.support_number || "",
      planCoverageInfo:
        demographicsData?.planCoverageInfo?.filter((coverage) =>
          isCoverageActiveOrExtended(coverage),
        ) || [],
      lat: Number(demographicsData?.latitude),
      lng: Number(demographicsData?.longitude),
      plainEmployeeId: demographicsData?.plainEmployeeId || "",
      mbrEffDate: demographicsData?.mbrEffDate || "",
      mbrTermDate: demographicsData?.mbrTermDate || "",
      address: [
        demographicsData?.city,
        demographicsData?.state,
        demographicsData?.zipCode,
      ]
        .filter(Boolean)
        .join(", "),
      mbrStreet1: demographicsData?.mbrStreet1 || "",
      mbrStreet2: demographicsData?.mbrStreet2 || "",
      city: demographicsData?.city || "",
      state: demographicsData?.state || "",
      chatbotDetails: {
        serviceDisplayEmail:
          groupData?.chatbotDetails?.serviceDisplayEmail || "",
        serviceProviderWebsite:
          groupData?.chatbotDetails?.serviceProviderWebsite || "",
        serviceProviderEmail:
          groupData?.chatbotDetails?.serviceProviderEmail || "",
        serviceProvider: groupData?.chatbotDetails?.serviceProvider || "",
        serviceProviderPhoneNumber:
          groupData?.chatbotDetails?.serviceProviderPhoneNumber || "",
        displayWebsite: groupData?.chatbotDetails?.displayWebsite || "",
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [authData?.user?.access_token, demographicsData?.memberId],
  );

  return info;
};

export default useUserInfo;
